import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { confirmPopup } from "primereact/confirmpopup";
import { InputText } from "primereact/inputtext";
import { useContext } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ToastContext } from "src/core/context/ToastContext";
import { logService } from "src/core/services/logService";
import { useAuthStore } from "src/features/auth/services/useAuthStore";
import { useProfileStore } from "../../services/useProfileStore";
import "./ProfileDeletePage.css";
import { useError } from "src/core/hooks/useError";
import { useErrorHandler } from "src/core/error/useErrorHandler";

export interface ProfileDeleteFormData {
  delete: string;
}

export function ProfileDeletePage() {
  const toastRef = useContext(ToastContext);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getErrorMessage } = useError();

  const authStore = useAuthStore();
  const profileStore = useProfileStore();
  const errorHandler = useErrorHandler();

  const form = useForm<ProfileDeleteFormData>({
    mode: "all",
  });

  const onDelete = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!form.formState.isValid) return;

    logService.log("ProfileDeletePage.onDelete");

    confirmPopup({
      target: event.currentTarget,
      message: t("Home.Profile.DeleteSure"),
      icon: "pi pi-question-circle",
      accept: async () => {
        await profileStore
          .deleteUser()
          .then(() => {
            authStore.logout();

            toastRef?.current?.show({
              severity: "success",
              summary: t("Home.Profile.DeleteTitle"),
              detail: t("Home.Profile.DeleteSuccess"),
              life: 5000,
            });

            navigate("/");
          })
          .catch((error) => {
            errorHandler.setError({
              title: t("Home.Profile.DeleteTitle"),
              message:
                t("Home.Profile.DeleteFailed") + " " + getErrorMessage(error),
            });
          });
      },
    });
  };

  return (
    <form>
      <div className="profileDeletePage flex justify-content-center">
        <Card
          title={t("Home.Profile.DeleteTitle")}
          className="card"
          footer={
            <div className="flex gap-2">
              <div className="flex-grow-1">
                <Button
                  type="button"
                  label={t("Base.Delete")}
                  icon="pi pi-trash"
                  severity="danger"
                  disabled={!form.formState.isValid}
                  loading={profileStore.loading}
                  onClick={onDelete}
                ></Button>
              </div>
              <div className="flex justify-content-between gap-2">
                <Button
                  type="button"
                  label={t("Base.Cancel")}
                  icon="pi pi-times"
                  severity="warning"
                  onClick={() => navigate("/profile")}
                ></Button>
              </div>
            </div>
          }
        >
          <label className="col-12 block font-bold">
            {t("Home.Profile.DeleteText1")}
          </label>

          <label className="col-12 block font-bold">
            {t("Home.Profile.DeleteText2")}
          </label>

          <div className="field grid justify-content-center">
            <div className="col-4 sm:col-3">
              <InputText
                id="pro_delete"
                className="w100"
                invalid={!!form.formState.errors.delete}
                {...form.register("delete", {
                  required: t("Error.Required"),
                  validate: (value) => {
                    return (
                      value === t("Home.Profile.DeleteWord") ||
                      t("Error.Pattern")
                    );
                  },
                })}
                type="text"
              />
              {form.formState.errors.delete && (
                <small className="p-error block">
                  {form.formState.errors.delete.message}
                </small>
              )}
            </div>
          </div>
        </Card>
      </div>
    </form>
  );
}
